import {
  hasYesOrNo,
  checkValueIncluded,
  dateIsEmpty,
  nameIsEmpty,
  locationCityStateIsEmpty,
  locationCityCountryIsEmpty,
  valueIsEmpty,
} from 'models/validate';

import locationCityStateModelValidator from 'forms/shared/validators/models/location/models/cityState';
import nameModelValidator from 'forms/shared/validators/models/name/name';
import locationBirthplaceWithoutCountyModelValidator from 'forms/shared/validators/models/location/models/birthplaceWithoutCounty';
import locationUSAddressModelValidator from 'forms/shared/validators/models/location/models/usAddress';
import * as formTypes from 'constants/formTypes';
import { FormDetails } from 'util/singletons';
import { isPVQForm } from 'helpers/form';
import {
  FOREIGN_BORN,
  NATURALIZED,
  DERIVED,
  NOT_CITIZEN,
  citizenshipStatusOptions,
  pvqCitizenshipStatusOptions,
  foreignBornDocumentTypes,
  notCitizenDocumentTypes,
  PVQ_FOREIGN_BORN,
  PVQ_NATURALIZED,
  PVQ_DERIVED,
  isNationalOrNonCitizen
} from 'constants/enums/citizenshipOptions';
import { DEFAULT_LATEST } from 'constants/dateLimits';
import { ValidateJSModel, ValidateJSOptions } from 'forms/shared/types';
import { lettersAndNumbersPattern, lettersAndNumbersPatternExceptOnlyZero } from 'constants/patterns';
import {
  certificateIsEmpty, documentationIsEmpty, form240IsEmpty, certificateOfNaturalizationIsEmpty
} from 'selectors/misc';
import { DeepSafePartial } from 'types/types';
import pvqNameModelValidator from 'forms/shared/validators/models/pvqName/name';
import { countryString } from '../../../../validators/location';
import { CitizenshipStatusModel } from './types';

const alphanumericRule = {
  presence: true,
  hasValue: {
    validator: {
      format: {
        pattern: lettersAndNumbersPattern,
        message: 'error.alphanumeric.pattern',
      },
      length: {
        maximum: 30,
        message: 'error.maximumLength',
      },
    },
  },
};

export const requireCertificateOfNaturalizationFields = (
  attributes: DeepSafePartial<CitizenshipStatusModel>,
  options: ValidateJSOptions,
  value: any,
): boolean => {
  if (
    attributes?.CitizenshipStatus?.value === NATURALIZED
    && options?.requireCitizenshipStatusNaturalizedFields
    && !options?.requireCitizenshipStatusCitizenshipCertificateFields
    && (
      attributes?.HasAlienRegistration?.value === 'No'
      || !attributes?.AlienRegistrationNumber?.value
      || !certificateOfNaturalizationIsEmpty(attributes)
      || value?.value
    )
  ) {
    return true;
  }
  return false;
};

export const requireCertificateFields = (
  attributes: DeepSafePartial<CitizenshipStatusModel>,
  options: ValidateJSOptions
): boolean => {
  if (attributes?.CitizenshipStatus?.value === FOREIGN_BORN && options?.requireForeignBornDocumentation) {
    const certificateEmpty = certificateIsEmpty(attributes, options);
    const documentationEmpty = documentationIsEmpty(attributes, options);
    if (!certificateEmpty || (documentationEmpty && certificateEmpty)) { return true; }
  }
  // SF85 v2013
  if (options?.requireCitizenshipStatusCitizenshipCertificateFields
    && attributes?.CitizenshipStatus?.value === NATURALIZED) {
    if (options?.hasValidUSPassport && certificateIsEmpty(attributes, options)) {
      return false;
    }
    if (
      !certificateIsEmpty(attributes, options)
      || (documentationIsEmpty(attributes, options) && form240IsEmpty(attributes))
    ) { return true; }
  }

  return false;
};

export const requireDocumentationFields = (
  attributes: DeepSafePartial<CitizenshipStatusModel>,
  options: ValidateJSOptions
): boolean => {
  if (attributes?.CitizenshipStatus?.value === FOREIGN_BORN && options?.requireForeignBornDocumentation) {
    const certificateEmpty = certificateIsEmpty(attributes, options);
    const documentationEmpty = documentationIsEmpty(attributes, options);
    if (!documentationEmpty || (certificateEmpty && documentationEmpty)) { return true; }
  }

  // SF85 v2013
  if (options?.requireCitizenshipStatusCitizenshipCertificateFields
    && attributes?.CitizenshipStatus?.value === NATURALIZED) {
    if (options?.hasValidUSPassport && documentationIsEmpty(attributes, options)) {
      return false;
    }
    if (
      !documentationIsEmpty(attributes, options)
      || (certificateIsEmpty(attributes, options) && form240IsEmpty(attributes))
    ) { return true; }
  }
  return false;
};

export const requireOtherDocumentationField = (
  attributes: DeepSafePartial<CitizenshipStatusModel>,
  attributeName: 'DocumentNumber' | 'DocumentIssued' | 'PlaceIssued' | 'DocumentName',
  options: ValidateJSOptions,
): boolean => {
  if (
    attributes?.CitizenshipStatus?.value === FOREIGN_BORN
    && options?.requireForeignBornDocumentation
    && attributes?.AbroadDocumentation?.value === 'Other'
  ) {
    if (attributeName === 'DocumentNumber') {
      return !valueIsEmpty(attributes?.DocumentNumber);
    }
    if (attributeName === 'DocumentIssued') {
      return !dateIsEmpty(attributes?.DocumentIssued);
    }
    if (attributeName === 'PlaceIssued') {
      return countryString(attributes?.PlaceIssued?.country) === 'United States'
        ? !locationCityStateIsEmpty(attributes?.PlaceIssued)
        : !locationCityCountryIsEmpty(attributes?.PlaceIssued, true);
    }
    if (attributeName === 'DocumentName') {
      return !nameIsEmpty(attributes?.DocumentName);
    }
  }

  return false;
};

export const requireForm240Fields = (
  attributes: DeepSafePartial<CitizenshipStatusModel>,
  options: ValidateJSOptions
): boolean => {
  if (
    options?.requireCitizenshipStatusForm240Date
    && options?.requireCitizenshipStatusForm240DateExplanation
  ) {
    if (attributes?.CitizenshipStatus?.value === NATURALIZED) {
      if (options?.hasValidUSPassport && form240IsEmpty(attributes)) {
        return false;
      }
      if (
        !form240IsEmpty(attributes)
        || (documentationIsEmpty(attributes, options) && certificateIsEmpty(attributes, options))
      ) { return true; }
    }
  }

  return false;
};

const citizenshipStatusValidator: ValidateJSModel<CitizenshipStatusModel> = {
  CitizenshipStatus: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {
        hasValue: {
          validator: { inclusion: pvqCitizenshipStatusOptions },
        },
      };
    }

    return {
      hasValue: {
        validator: { inclusion: citizenshipStatusOptions },
      },
    };
  },

  CitizenshipStatusComments: {},
  AlienRegistrationComments: {},
  AlienRegistrationNumberComments: {},
  DerivedComments: {},
  MilitaryBaseComments: {},
  ProofOfUSCitizenshipComments: {},
  CertificateComments: {},
  NaturalizationComments: {},

  BornOnMilitaryInstallation: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      if (attributes?.CitizenshipStatus?.value === PVQ_FOREIGN_BORN) {
        return {
          hasValue: { validator: hasYesOrNo }
        };
      }

      return {};
    }

    if (attributes?.CitizenshipStatus?.value === FOREIGN_BORN) {
      return {
        hasValue: { validator: hasYesOrNo },
      };
    }

    return {};
  },

  MilitaryBase: (value, attributes) => {
    if (attributes?.BornOnMilitaryInstallation?.value === 'Yes') { return { hasValue: true }; }

    return {};
  },

  AbroadDocumentation: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      if (attributes?.CitizenshipStatus?.value === PVQ_FOREIGN_BORN) {
        return {
          hasValue: {
            validator: { inclusion: foreignBornDocumentTypes },
          },
        };
      }

      return {};
    }

    if (requireDocumentationFields(attributes, options)
      && !options?.requireCitizenshipStatusCitizenshipCertificateFields // this field is not required for SF85 v2013
    ) {
      return {
        hasValue: {
          validator: { inclusion: foreignBornDocumentTypes },
        },
      };
    }

    return {};
  },

  Explanation: {},

  DocumentTitle: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      if ((attributes?.AbroadDocumentation?.value === 'Other'
      && !options?.requireCitizenshipStatusCitizenshipCertificateFields
      && checkValueIncluded(attributes?.CitizenshipStatus, [FOREIGN_BORN])
      && options?.requireForeignBornDocumentation)
      || (attributes?.Basis?.value === 'Other'
        && ((checkValueIncluded(attributes?.CitizenshipStatus, [NATURALIZED])
          && options?.requireCitizenshipStatusNaturalizedFields)
          || checkValueIncluded(attributes?.CitizenshipStatus, [DERIVED])))
      || (attributes?.DocumentType?.value === 'Other'
        && (attributes?.CitizenshipStatus?.value === NOT_CITIZEN
          && options?.requireCitizenshipStatusNotCitizenDocumentFields))) { return { hasValue: true }; }

      return {};
    }

    return {};
  },

  DocumentNumber: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      if (attributes?.DocumentNumberNotApplicable?.applicable === false) {
        return {};
      }

      if (attributes?.CitizenshipStatus?.value === PVQ_FOREIGN_BORN
        || (isNationalOrNonCitizen(attributes?.CitizenshipStatus?.value) && attributes.IsResidentOfUnitedStates?.value === 'Yes')) {
        return alphanumericRule;
      }

      return {};
    }

    if (options?.requiredDocumentsEmptySF852013) {
      return {
        requireTrue: { // force error message; value will never be true
          message: 'citizenship.errors.missingRequiredDocumentsSF852013'
        }
      };
    }

    if ((requireDocumentationFields(attributes, options)
      && attributes?.AbroadDocumentation?.value !== 'Other')
      || requireOtherDocumentationField(attributes, attributeName, options)
      || (attributes?.CitizenshipStatus?.value === NOT_CITIZEN && attributes?.DocumentType?.value !== 'Other'
        && options?.requireCitizenshipStatusNotCitizenDocumentFields)) { return alphanumericRule; }

    return {};
  },

  DocumentIssued: (value, attributes, attributeName, options = {}) => {
    if (isPVQForm(options?.formType)) {
      if (attributes?.CitizenshipStatus?.value === PVQ_FOREIGN_BORN
        || (isNationalOrNonCitizen(attributes?.CitizenshipStatus?.value) && attributes.IsResidentOfUnitedStates?.value === 'Yes')) {
        return {
          presence: true,
          date: { earliest: options?.applicantBirthdate, latest: DEFAULT_LATEST },
        };
      }

      return {};
    }

    if ((requireDocumentationFields(attributes, options)
      && attributes?.AbroadDocumentation?.value !== 'Other')
      || requireOtherDocumentationField(attributes, attributeName, options)
      || (attributes?.CitizenshipStatus?.value === NOT_CITIZEN && attributes?.DocumentType?.value !== 'Other'
        && options?.requireCitizenshipStatusNotCitizenDocumentFields)) {
      return {
        presence: true,
        date: { earliest: options?.applicantBirthdate, latest: DEFAULT_LATEST },
      };
    }

    return {};
  },

  PlaceIssued: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      if (attributes?.CitizenshipStatus?.value === PVQ_FOREIGN_BORN) {
        if (value?.inUSA === true) { return {}; }
        return {
          presence: true,
          location: { validator: locationBirthplaceWithoutCountyModelValidator },
        };
      }
      return {};
    }

    if ((requireDocumentationFields(attributes, options)
      && attributes?.AbroadDocumentation?.value !== 'Other')
      || requireOtherDocumentationField(attributes, attributeName, options)) {
      if (options?.requireCitizenshipStatusCitizenshipCertificateFields) {
        // SF85 v2013
        return {
          presence: true,
          location: { validator: locationCityStateModelValidator },
        };
      }
      return {
        presence: true,
        location: { validator: locationBirthplaceWithoutCountyModelValidator },
      };
    }

    return {};
  },

  DocumentName: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      if (attributes?.CitizenshipStatus?.value === PVQ_FOREIGN_BORN
        || (isNationalOrNonCitizen(attributes?.CitizenshipStatus?.value) && attributes.IsResidentOfUnitedStates?.value === 'Yes')) {
        return {
          presence: true,
          model: { validator: pvqNameModelValidator },
        };
      }

      return {};
    }

    if ((requireDocumentationFields(attributes, options)
      && !options?.requireCitizenshipStatusCitizenshipCertificateFields // this field is not required for SF85 v2013
      && attributes?.AbroadDocumentation?.value !== 'Other')
      || requireOtherDocumentationField(attributes, attributeName, options)
      || (attributes?.CitizenshipStatus?.value === NOT_CITIZEN && attributes?.DocumentType?.value !== 'Other'
        && options?.requireCitizenshipStatusNotCitizenDocumentFields)) {
      return {
        presence: true,
        model: { validator: nameModelValidator },
      };
    }

    return {};
  },

  CertificateNumber: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType) && attributes?.CitizenshipCertificateCardNumberNotApplicable?.applicable === false) {
      return {};
    }
    if (isPVQForm(options?.formType) && [PVQ_NATURALIZED, PVQ_DERIVED].includes(attributes?.CitizenshipStatus?.value || '')) {
      return alphanumericRule;
    }

    if (options?.requiredDocumentsEmptySF852013) {
      return {
        requireTrue: {
          message: 'citizenship.errors.missingRequiredDocumentsSF852013'
        }
      };
    }

    if (requireCertificateFields(attributes, options) // ForeignBorn
    ) { return alphanumericRule; }

    if (requireCertificateOfNaturalizationFields(attributes, options, value)) { // Naturalized
      return {
        presence: true,
        hasValue: {
          validator: {
            format: {
              pattern: lettersAndNumbersPatternExceptOnlyZero,
              message: 'error.alphanumeric.patternExceptOnlyZero',
            },
            length: {
              maximum: 30,
              message: 'error.maximumLength',
            },
          },
        },
      };
    }

    if (attributes?.CitizenshipStatus?.value === DERIVED
      && (value?.value
        || (!attributes?.PermanentResidentCardNumber?.value
          && !attributes?.AlienRegistrationNumber?.value))) { return alphanumericRule; }

    return {};
  },

  CertificateIssued: (value, attributes, attributeName, options = {}) => {
    if (isPVQForm(options?.formType) && [PVQ_NATURALIZED, PVQ_DERIVED].includes(attributes?.CitizenshipStatus?.value || '')) {
      return {
        presence: true,
        date: { earliest: options?.applicantBirthdate, latest: DEFAULT_LATEST },
      };
    }
    if (requireCertificateFields(attributes, options) // ForeignBorn
      || requireCertificateOfNaturalizationFields(attributes, options, value) // Naturalized
      || attributes?.CitizenshipStatus?.value === DERIVED) {
      return {
        presence: true,
        date: { earliest: options?.applicantBirthdate, latest: DEFAULT_LATEST },
      };
    }

    return {};
  },

  CertificateName: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType) && [PVQ_NATURALIZED, PVQ_DERIVED].includes(attributes?.CitizenshipStatus?.value || '')) {
      return { presence: true, model: { validator: pvqNameModelValidator } };
    }
    if (options?.requireCitizenshipStatusCitizenshipCertificateFields) { return {}; }

    if (requireCertificateFields(attributes, options) // ForeignBorn
      || requireCertificateOfNaturalizationFields(attributes, options, value) // Naturalized
      || attributes?.CitizenshipStatus?.value === DERIVED) { return { presence: true, model: { validator: nameModelValidator } }; }

    return {};
  },

  CertificateMotherMaidenName: (value, attributes, attributeName, options) => {
    const formDetails = FormDetails.getInstance().getData();

    if (options?.formType === formTypes.SF85 && formDetails.formVersion !== '2013-12') {
      return { presence: true, model: { validator: nameModelValidator } };
    }
    return {};
  },

  EntryDate: (value, attributes, attributeName, options = {}) => {
    if (isPVQForm(options?.formType)) {
      if (isNationalOrNonCitizen(attributes?.CitizenshipStatus?.value) && attributes.EntryDateNotApplicable?.applicable !== false) {
        return {
          presence: true,
          date: { earliest: options?.applicantBirthdate, latest: DEFAULT_LATEST },
        };
      }

      return {};
    }

    if ((checkValueIncluded(attributes?.CitizenshipStatus, [NATURALIZED])
      && options?.requireCitizenshipStatusNaturalizedFields)
      || (checkValueIncluded(attributes?.CitizenshipStatus, [NOT_CITIZEN])
        && attributes?.EntryDateNotApplicable?.applicable !== false)) {
      return {
        presence: true,
        date: { earliest: options?.applicantBirthdate, latest: DEFAULT_LATEST },
      };
    }

    return {};
  },

  EntryLocation: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      if (isNationalOrNonCitizen(attributes?.CitizenshipStatus?.value) && attributes.EntryDateNotApplicable?.applicable !== false) {
        return {
          presence: true,
          location: {
            validator: locationCityStateModelValidator
          }
        };
      }

      return {};
    }

    if ((checkValueIncluded(attributes?.CitizenshipStatus, [NATURALIZED])
      && options?.requireCitizenshipStatusNaturalizedFields)
      || (checkValueIncluded(attributes?.CitizenshipStatus, [NOT_CITIZEN])
        && attributes?.EntryLocationNotApplicable?.applicable !== false)) {
      return {
        presence: true,
        location: { validator: locationCityStateModelValidator }
      };
    }

    return {};
  },

  PriorCitizenship: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      if (isNationalOrNonCitizen(attributes?.CitizenshipStatus?.value)) {
        return {
          hasValue: true,
          country: { excludeUnitedStates: true },
        };
      }

      return {};
    }
    if ((checkValueIncluded(attributes?.CitizenshipStatus, [NATURALIZED])
      && options?.requireCitizenshipStatusNaturalizedFields)
      || checkValueIncluded(attributes?.CitizenshipStatus, [NOT_CITIZEN])) {
      return {
        hasValue: true,
        country: { excludeUnitedStates: true },
      };
    }

    return {};
  },

  HasAlienRegistration: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) { return {}; }
    if (attributes?.CitizenshipStatus?.value === NATURALIZED
      && options?.requireCitizenshipStatusNaturalizedFields) {
      return {
        hasValue: { validator: hasYesOrNo },
      };
    }

    return {};
  },

  AlienRegistrationNumber: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      if (attributes?.AlienRegistrationNumberNotApplicable?.applicable === false) {
        return {};
      }
      if ([PVQ_NATURALIZED, PVQ_DERIVED].includes(attributes?.CitizenshipStatus?.value || '')) {
        return alphanumericRule; // TODO: this will need reviewed when foreigners are allowed to enter the PVQ as this is invalid for a foreign National.
      }

      if (isNationalOrNonCitizen(attributes?.CitizenshipStatus?.value) && attributes.IsResidentOfUnitedStates?.value === 'Yes') {
        return alphanumericRule;
      }

      return {};
    }
    if (attributes?.HasAlienRegistration?.value === 'Yes'
      && attributes?.CitizenshipStatus?.value === NATURALIZED) { return alphanumericRule; }

    if (attributes?.CitizenshipStatus?.value === DERIVED
      && (value?.value
        || (!attributes?.PermanentResidentCardNumber?.value
          && !attributes?.CertificateNumber?.value))) { return alphanumericRule; }

    if (attributes?.CitizenshipStatus?.value === NOT_CITIZEN
      && attributes?.AlienRegistrationNumberNotApplicable?.applicable !== false) { return alphanumericRule; }

    return {};
  },

  AlienRegistrationExpiration: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      if (isNationalOrNonCitizen(attributes?.CitizenshipStatus?.value) && attributes.IsResidentOfUnitedStates?.value === 'Yes') {
        return {
          presence: true,
          date: {
            earliest: (attributes?.DocumentIssued || options?.applicantBirthdate),
            tooEarly: 'citizenship.errors.alienregistrationexpiration.tooearly',
          }
        };
      }

      return {};
    }

    if (attributes?.CitizenshipStatus?.value === NOT_CITIZEN
      && options?.requireCitizenshipStatusAlienRegistrationExpiration
      && attributes?.DocumentType?.value === 'U.S. Visa') {
      return {
        presence: true,
        date: {
          earliest: (attributes?.DocumentIssued || options?.applicantBirthdate),
          tooEarly: 'citizenship.errors.alienregistrationexpiration.tooearly',
        }
      };
    }
    return {};
  },

  CertificateCourtName: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) { return {}; }
    if (options?.requireCitizenshipStatusCitizenshipCertificateFields
      && requireCertificateFields(attributes, options)) { return { hasValue: true }; }

    // Natrualized
    if (requireCertificateOfNaturalizationFields(attributes, options, value)) {
      return { hasValue: true };
    }

    return {};
  },

  CertificateCourtAddress: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) { return {}; }
    if (options?.requireCitizenshipStatusCitizenshipCertificateFields
      && requireCertificateFields(attributes, options)) { return { presence: true, location: { validator: locationUSAddressModelValidator } }; }

    // Naturalized
    if (requireCertificateOfNaturalizationFields(attributes, options, value)) {
      return { presence: true, location: { validator: locationUSAddressModelValidator } };
    }

    return {};
  },

  Basis: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) { return {}; }
    if (requireCertificateOfNaturalizationFields(attributes, options, value) // Naturalized
      || checkValueIncluded(attributes?.CitizenshipStatus, [DERIVED])) { return { hasValue: true }; }
    return {};
  },

  PermanentResidentCardNumber: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType) && attributes?.CitizenshipStatus?.value === PVQ_DERIVED) {
      return alphanumericRule;
    }
    if (attributes?.CitizenshipStatus?.value === DERIVED
      && (value?.value
        || (!attributes?.AlienRegistrationNumber?.value
          && !attributes?.CertificateNumber?.value))) { return alphanumericRule; }

    return {};
  },

  IsResidentOfUnitedStates: (value, attributes, attributeName, options = {}) => {
    if (isPVQForm(options?.formType) && isNationalOrNonCitizen(attributes?.CitizenshipStatus?.value)) {
      return {
        hasValue: { validator: hasYesOrNo },
      };
    }

    return {};
  },

  ResidenceStatus: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      if (isNationalOrNonCitizen(attributes?.CitizenshipStatus?.value) && attributes.IsResidentOfUnitedStates?.value === 'Yes') {
        return {
          hasValue: true,
        };
      }

      return {};
    }

    if (attributes?.CitizenshipStatus?.value === NOT_CITIZEN
      && options?.requireCitizenshipStatusResidenceStatus) { return { hasValue: true }; }

    return {};
  },

  ResidenceStatusExplanation: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType) && attributes.IsResidentOfUnitedStates?.value === 'Yes' && attributes?.ResidenceStatus?.value === 'Other') {
      return { hasValue: true };
    }
    return {};
  },

  DocumentType: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      if (isNationalOrNonCitizen(attributes?.CitizenshipStatus?.value) && attributes.IsResidentOfUnitedStates?.value === 'Yes') {
        return {
          hasValue: {
            validator: { inclusion: notCitizenDocumentTypes },
          },
        };
      }

      return {};
    }

    if (attributes?.CitizenshipStatus?.value === NOT_CITIZEN
      && options?.requireCitizenshipStatusNotCitizenDocumentFields) {
      return {
        hasValue: {
          validator: { inclusion: notCitizenDocumentTypes },
        },
      };
    }

    return {};
  },

  DocumentExpiration: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      if (attributes?.DocumentExpirationNotApplicable?.applicable === false) {
        return {};
      }

      if (isNationalOrNonCitizen(attributes?.CitizenshipStatus?.value) && attributes.IsResidentOfUnitedStates?.value === 'Yes') {
        return {
          date: {
            earliest: options?.applicantBirthdate
          }
        };
      }

      return {};
    }

    if (!dateIsEmpty(value) || value?.estimated) { return { date: { earliest: options?.applicantBirthdate } }; }
    // date not forced but if it is there it needs to be valid
    return {};
  },

  MaidenName: (value, attributes, attributeName, options) => {
    if (options?.requireCitizenshipStatusMaidenName) { return { hasValue: true }; }

    return {};
  },
  Form240Date: (value, attributes, attributeName, options) => {
    if (options?.requiredDocumentsEmptySF852013) {
      return {
        requireTrue: {
          message: 'citizenship.errors.missingRequiredDocumentsSF852013'
        }
      };
    }
    if (requireForm240Fields(attributes, options)
      && checkValueIncluded(attributes?.CitizenshipStatus, [NATURALIZED])) {
      return {
        presence: true,
        date: {
          earliest: options?.applicantBirthdate,
          latest: DEFAULT_LATEST,
          tooEarly: 'error.date.min',
        },
      };
    }
    return {};
  },
  Form240DateExplanation: (value, attributes, attributeName, options) => {
    if (requireForm240Fields(attributes, options)
      && checkValueIncluded(attributes?.CitizenshipStatus, [NATURALIZED])) {
      return { hasValue: true };
    }

    return {};
  },

  EntryDateExplanation: (value, attributes, attributeName, options = {}) => {
    if (isPVQForm(options?.formType)) { return {}; }
    if (attributes?.EntryDateNotApplicable?.applicable === false
      && checkValueIncluded(attributes?.CitizenshipStatus, [NOT_CITIZEN])) {
      return {
        hasValue: true,
      };
    }

    return {};
  },
  EntryLocationExplanation: (value, attributes, attributeName, options = {}) => {
    if (attributes?.EntryLocationNotApplicable?.applicable === false
      && checkValueIncluded(attributes?.CitizenshipStatus, [NOT_CITIZEN])) {
      return {
        hasValue: true,
      };
    }

    return {};
  },
  AlienRegistrationNumberExplanation: (value, attributes, attributeName, options = {}) => {
    if (isPVQForm(options?.formType)) { return {}; }
    if (attributes?.AlienRegistrationNumberNotApplicable?.applicable === false
      && attributes?.CitizenshipStatus?.value === NOT_CITIZEN) {
      return { hasValue: true };
    }

    return {};
  },
  EntryDateNotApplicable: {},
  EntryLocationNotApplicable: {},
  AlienRegistrationNumberNotApplicable: {},
  DocumentNumberNotApplicable: {},
  CitizenshipCertificateCardNumberNotApplicable: {},
  DocumentExpirationNotApplicable: {},
};

export default citizenshipStatusValidator;
